import React, { useState } from "react";
import PropTypes from "prop-types";
import PublicationLinks from "../../../../../components/Publications/components/PublicationLinks";
import { colorPathogenicity } from "../helpers";
import { effectStrings } from "./constants";
import { capitalizeFirstLetter } from "../../../../../common/helpers/helpers";
import HelperText from "../../../../../components/atoms/HelperText";
import VariantsTable from "./VariantsTable";

const IndividualsTable = ({ individuals, varsomeHost }) => {
  const [expandedRows, setExpandedRows] = useState([]);

  const handleExpandRow = (e, index) => {
    const currentExpandedRows = expandedRows;
    const newExpandedRows = currentExpandedRows.includes(index)
      ? currentExpandedRows.filter((idx) => idx !== index)
      : currentExpandedRows.concat(index);
    setExpandedRows(newExpandedRows);
  };

  if (individuals && individuals.length > 0) {
    const title =
      individuals.length === 1 ? "Full submission" : "Full submissions";
    const text = `Data from literature or unpublished research. Includes all patient's relevant variants and additional patient information e.g phenotypes, sex, comments.`;
    return (
      <div
        className="varsome-inner-container tw-overflow-x-auto"
        data-testid="individuals-table"
      >
        <h6>
          {title}
          <HelperText customText={text} />
        </h6>
        <table className="varsome-table tw-table-auto">
          <thead>
            <tr>
              <th className="tw-w-3/20 tw-font-semibold">Variant</th>
              <th className="tw-w-3/20 tw-font-semibold">
                Clinical Classification
              </th>
              <th className="tw-w-3/20 tw-font-semibold">Effect Curator</th>
              <th className="tw-w-3/20 tw-font-semibold">Effect Submitter</th>
              <th className="tw-w-3/20 tw-font-semibold">Comments</th>
              <th className="tw-w-2/20 tw-font-semibold">Phenotypes</th>
              <th className="tw-w-1/20 tw-font-semibold">Sex</th>
              <th className="tw-w-1/20 tw-font-semibold">Publications</th>
              <th className="tw-w-1/20 tw-font-semibold">Other variants</th>
            </tr>
          </thead>
          <tbody data-testid="individuals-table-body">
            {individuals?.map((individual, index) => {
              const currentVariants = individual?.variants?.filter(
                (variant) => {
                  return variant?.is_current === true;
                }
              );
              const currentVariant = currentVariants?.[0] || null;
              const otherVariants = individual?.variants?.filter((variant) => {
                return variant?.is_current === false;
              });
              return (
                <React.Fragment key={`fragment-${index}`}>
                  <tr key={`row-${index}`}>
                    <td>
                      {currentVariant?.variant_info?.map((v, i) => {
                        const variantUrl = `${varsomeHost}/variant/${v?.variant_id}`;
                        if (v?.variant_notation) {
                          return (
                            <a
                              key={`variant-${i}`}
                              href={variantUrl}
                              target="_blank"
                            >
                              {v?.variant_notation}
                            </a>
                          );
                        }
                        return null;
                      })}
                      <br />
                      <small>
                        <a href={currentVariant?.lovd_link} target="_blank">
                          See in LOVD
                        </a>
                      </small>
                    </td>
                    <td data-testid="individuals-table-classification-submitter">
                      {currentVariant?.pathogenicity
                        ?.clinical_classification_submitter
                        ? colorPathogenicity(
                            currentVariant?.pathogenicity
                              ?.clinical_classification_submitter
                          )
                        : "-"}
                    </td>
                    <td data-testid="individuals-table-effect-curator">
                      {currentVariant?.pathogenicity?.effect_curator
                        ? effectStrings[
                            currentVariant?.pathogenicity?.effect_curator
                          ] || currentVariant?.pathogenicity?.effect_curator
                        : "-"}
                    </td>
                    <td data-testid="individuals-table-effect-submitter">
                      {currentVariant?.pathogenicity?.effect_submitter
                        ? effectStrings[
                            currentVariant?.pathogenicity?.effect_submitter
                          ] || currentVariant?.pathogenicity?.effect_submitter
                        : "-"}
                    </td>
                    <td>
                      {individual?.comments?.length > 0 ? (
                        <div data-testid="individuals-table-comments">
                          {individual?.comments?.map((comment, idx) => {
                            return (
                              <div key={`comment-${idx}`}>
                                {comment && capitalizeFirstLetter(comment)}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {individual?.phenotypes?.length > 0 ? (
                        <div data-testid="individuals-table-phenotypes">
                          {individual?.phenotypes?.map((phenotype, idx) => {
                            return (
                              <div key={`phenotype-${idx}`}>
                                {phenotype && capitalizeFirstLetter(phenotype)}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td data-testid="individuals-table-gender">
                      {(individual?.gender &&
                        capitalizeFirstLetter(individual?.gender)) ||
                        "-"}
                    </td>
                    <td>
                      {individual?.pub_med_references ? (
                        <PublicationLinks
                          limit={10}
                          ids={individual?.pub_med_references}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td data-testid={`individuals-other-variants-${index}`}>
                      {otherVariants && otherVariants.length > 0 ? (
                        <a
                          onClick={(event) => handleExpandRow(event, index)}
                          data-testid={`variants-table-button-${index}`}
                        >
                          {expandedRows.includes(index) ? "Hide" : "Show"}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                  {expandedRows.includes(index) && (
                    <tr key={`row-${index}-expanded`}>
                      <td colSpan="9" className="tw-p-0">
                        <VariantsTable
                          variants={otherVariants}
                          varsomeHost={varsomeHost}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};

IndividualsTable.propTypes = {
  individuals: PropTypes.arrayOf(
    PropTypes.shape({
      comments: PropTypes.arrayOf(PropTypes.string),
      gender: PropTypes.string,
      phenotypes: PropTypes.arrayOf(PropTypes.string),
      pub_med_references: PropTypes.arrayOf(PropTypes.number),
      variants: PropTypes.arrayOf(
        PropTypes.shape({
          comments: PropTypes.arrayOf(PropTypes.string),
          is_current: PropTypes.bool,
          is_lifted_over: PropTypes.bool,
          lovd_link: PropTypes.string,
          pathogenicity: PropTypes.shape({
            clinical_classification_submitter: PropTypes.string,
            clinical_classification_curator: PropTypes.string,
            effect_submitter: PropTypes.string,
            effect_curator: PropTypes.string,
            effect_symbol: PropTypes.string,
          }),
          variant_info: PropTypes.arrayOf(
            PropTypes.shape({
              variant_id: PropTypes.string,
              variant_notation: PropTypes.string,
            })
          ),
        })
      ),
    })
  ),
  varsomeHost: PropTypes.string,
};

IndividualsTable.defaultProps = {
  individuals: [],
  varsomeHost: null,
};

export default IndividualsTable;
